import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { SphereAtmosphere, SphereDefs, SphereGlass } from './Sphere';
import PlanetSurface, { SurfaceDefs } from './PlanetSurface';

/*
 * SystemMark -- the site's visual signature.
 *
 * A banded core encircled by tilted orbits. The bands are strata (layers of a
 * system, read bottom-up) and the rings are orbits carrying satellites. Both
 * can be driven by real values, so the same construction is a logo at 16px and
 * a chart at 400px.
 *
 * Drawn inline so it inherits the theme, stays sharp at any size, and costs
 * nothing over the wire.
 */

const VB = 200; // viewBox is square; callers size it with CSS
const C = VB / 2;

/** Front half of a tilted orbit, drawn over the core so the ring wraps it. */
const frontArc = (rx, ry) => `M ${-rx},0 A ${rx},${ry} 0 0 0 ${rx},0`;

const satellitePos = (angleDeg, rx, ry) => {
  const t = (angleDeg * Math.PI) / 180;
  return [rx * Math.cos(t), ry * Math.sin(t)];
};

const DEFAULT_PERIOD = 22; // seconds for one satellite circuit

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

const SystemMark = ({
  surface = 'bands',
  tilt = 0,
  color = 'var(--tmc-brand-core)',
  rings = [],
  coreRadius = 52,
  label,
  title,
  className = '',
}) => {
  const uid = useId().replace(/:/g, '');
  const clipId = `sm-core-${uid}`;
  const reduced = usePrefersReducedMotion();
  const satRefs = useRef([]);
  const backRef = useRef(null);
  const frontRef = useRef(null);

  // One flat list, so each satellite is a single element that moves between the
  // behind-the-core and in-front-of-the-core layers as it comes round.
  const satellites = rings.flatMap((ring, ringIndex) =>
    (ring.satellites ?? []).map((sat, index) => ({
      ...sat,
      key: `${ringIndex}-${index}`,
      rx: ring.rx,
      ry: ring.ry,
      tilt: ring.tilt ?? -18,
      period: ring.period ?? DEFAULT_PERIOD * (1 + ringIndex * 0.4),
    })),
  );

  useLayoutEffect(() => {
    const nodes = satRefs.current;
    if (!nodes.length) return undefined;

    const place = (elapsed) => {
      satellites.forEach((sat, index) => {
        const node = nodes[index];
        if (!node) return;
        const deg = sat.angle + (elapsed / sat.period) * 360;
        const [lx, ly] = satellitePos(deg, sat.rx, sat.ry);
        const rad = (sat.tilt * Math.PI) / 180;
        const x = C + lx * Math.cos(rad) - ly * Math.sin(rad);
        const y = C + lx * Math.sin(rad) + ly * Math.cos(rad);
        // depth runs -1 (far side) to +1 (nearest the viewer)
        const depth = Math.sin((deg * Math.PI) / 180);
        const scale = 1 + 0.2 * depth;
        node.setAttribute(
          'transform',
          `translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(${scale.toFixed(3)})`,
        );
        // Continuous, so an orb never changes appearance the moment it crosses
        // the centre line; it just recedes.
        node.style.opacity = (0.78 + 0.22 * ((depth + 1) / 2)).toFixed(3);
        const layer = depth > 0 ? frontRef.current : backRef.current;
        if (layer && node.parentNode !== layer) layer.appendChild(node);
      });
    };

    if (reduced) {
      place(0);
      return undefined;
    }
    let frame;
    const started = performance.now();
    const tick = (now) => {
      place((now - started) / 1000);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [rings, reduced]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      className={`system-mark ${className}`.trim()}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx={C} cy={C} r={coreRadius} />
        </clipPath>
        <SphereDefs uid={uid} />
        <SurfaceDefs uid={uid} />
      </defs>

      {/* A faint atmosphere sets the core off its background. */}
      <SphereAtmosphere
        uid={uid}
        cx={C}
        cy={C}
        r={coreRadius}
        color={color}
      />

      {/* Orbits: full ellipse first, so the core occludes the far half. */}
      {rings.map((ring, index) => (
        <g key={`back-${index}`} transform={`translate(${C} ${C}) rotate(${ring.tilt ?? -18})`}>
          <ellipse className="sm-orbit" rx={ring.rx} ry={ring.ry} style={{ '--orbit': ring.color }} />
        </g>
      ))}

      {/* Satellites currently on the far side of the core. */}
      <g ref={backRef} />

      {/* The core: one procedural world. */}
      <g clipPath={`url(#${clipId})`}>
        <PlanetSurface
          uid={uid}
          cx={C}
          cy={C}
          r={coreRadius}
          color={color}
          surface={surface}
          tilt={tilt}
        />
      </g>
      <SphereGlass uid={uid} cx={C} cy={C} r={coreRadius} />

      {/* Near half of each orbit, drawn over the core. */}
      {rings.map((ring, index) => (
        <g key={`front-${index}`} transform={`translate(${C} ${C}) rotate(${ring.tilt ?? -18})`}>
          <path
            className="sm-orbit sm-orbit--front"
            d={frontArc(ring.rx, ring.ry)}
            style={{ '--orbit': ring.color }}
          />
        </g>
      ))}

      {/* Satellites on the near side, and the pool they are drawn from. */}
      <g ref={frontRef}>
        {satellites.map((sat, index) => (
          <g
            key={sat.key}
            ref={(node) => {
              satRefs.current[index] = node;
            }}
            className="sm-satellite"
          >
            <circle cx={0} cy={0} r={sat.r ?? 5} style={{ fill: sat.color }} />
            <SphereGlass uid={uid} cx={0} cy={0} r={sat.r ?? 5} />
          </g>
        ))}
      </g>

      {label && (
        <text className="sm-label" x={C} y={C + 5} textAnchor="middle">
          {label}
        </text>
      )}
    </svg>
  );
};

/*
 * The canonical brand configuration: five strata in the signal palette, one
 * orbit. Used wherever the mark stands for the site rather than for data.
 */
export const BRAND_SURFACE = 'bands';
export const BRAND_COLOR = 'var(--tmc-brand-core)';

export const BRAND_RINGS = [
  { tilt: -20, rx: 88, ry: 30, color: 'var(--tmc-signal-systems)' },
];

export default SystemMark;
